/**
 * @name LogoLoader
 * @author Keven Adan Bezerra | kevenadanb
 * @description Componente de loader com logo girando, utilizado para indicar carregamento.
 * Usa o tema do projeto (useThemeContext) para selecionar a logo apropriada (claro/escuro).
 * Inclui botão de alternar tema no canto superior direito para teste/desenvolvimento.
 * @returns {JSX.Element} - JSX do componente de loading.
 * @package src/contexts/LoadingContext/LogoLoader.tsx
 */

import { Box, Grid, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";
import logoDark from "../../assets/png/logo-dark.png";
import useThemeContext from "../../themes/hooks/useThemeContext";
import SimpleLogo from "./SimpleLogo";

const LogoLoader: React.FC = () => {
  const { theme } = useThemeContext();

  // Seleciona a logo baseada no tema
  const logoSrc = theme.palette.mode === "dark" ? logoDark : logoDark;

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      className="logo-loader-backdrop"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 9999,
      }}
    >
      <style>
        {`
                         /* Efeito de transição suave para o backdrop */
                         .logo-loader-backdrop {
                              background: linear-gradient(
                                   135deg, 
                                   ${alpha(theme.palette.background.default, 0.15)} 0%,
                                   ${alpha(theme.palette.background.paper, 0.1)} 50%,
                                   ${alpha(theme.palette.background.default, 0.2)} 100%
                              );
                              backdrop-filter: blur(12px) saturate(1.8) brightness(1.1);
                              -webkit-backdrop-filter: blur(12px) saturate(1.8) brightness(1.1);
                              animation: fadeInBackdrop 0.1s ease-out forwards;
                              opacity: 0;
                         }

                         @keyframes fadeInBackdrop {
                              0% {
                                   opacity: 0;
                                   backdrop-filter: blur(0px) saturate(1) brightness(1);
                                   -webkit-backdrop-filter: blur(0px) saturate(1) brightness(1);
                              }
                              100% {
                                   opacity: 1;
                                   backdrop-filter: blur(12px) saturate(1.8) brightness(1.1);
                                   -webkit-backdrop-filter: blur(12px) saturate(1.8) brightness(1.1);
                              }
                         }

                         /* Animação suave para o conteúdo do loader */
                         .logo-loader-content {
                              animation: fadeInContent 0.2s ease-out 0.05s forwards;
                              opacity: 0;
                              transform: translateY(20px) scale(0.9);
                         }

                         @keyframes fadeInContent {
                              0% {
                                   opacity: 0;
                                   transform: translateY(20px) scale(0.9);
                              }
                              100% {
                                   opacity: 1;
                                   transform: translateY(0px) scale(1);
                              }
                         }

                         /* Animação de rotação para o logo */
                         .rotating-logo {
                              animation: rotate 2s linear infinite;
                              filter: drop-shadow(0 0 20px rgba(25, 118, 210, 0.3));
                         }

                         @keyframes rotate {
                              0% {
                                   transform: rotate(0deg);
                              }
                              100% {
                                   transform: rotate(360deg);
                              }
                         }

                         @keyframes pulse {
                              0%, 100% {
                                   opacity: 0.8;
                              }
                              50% {
                                   opacity: 1;
                              }
                         }

                         /* Desabilita o cursor pointer e hover do logo durante o loading */
                         .rotating-logo img {
                              cursor: default !important;
                              pointer-events: none;
                         }
                         
                         .rotating-logo img:hover {
                              transform: none !important;
                         }
                    `}
      </style>

      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        className="logo-loader-content"
      >
        <Box className="rotating-logo">
          <SimpleLogo
            src={logoSrc}
            alt="Logo IS-Cantina"
            width={120}
            height={120}
          />
        </Box>
        <Typography
          mt={3}
          variant="overline"
          color={theme.palette.primary.main}
          fontSize={14}
          sx={{
            fontWeight: "bold",
            textShadow: "0 1px 3px rgba(0,0,0,0.2)",
            animation: "pulse 1.2s ease-in-out infinite",
          }}
          className="loading-text"
        >
          Carregando...
        </Typography>
      </Grid>
    </Grid>
  );
};

export default LogoLoader;
